import { Button, Flex, Spacer, Text } from "@chakra-ui/react";
import React, { useCallback, useEffect } from "react";
import { RiEditLine } from "react-icons/ri";
import { useLocation, useNavigate } from "react-router-dom";
import { AdminFormButtons } from "src/components/Layout/AdminFormButtons";
import { AdminPage } from "src/components/Layout/AdminPage";
import { Card } from "src/components/Layout/Card";
import { Breadcrumb } from "src/components/Navigation/Breadcrumb";
import { WithUserPermissions } from "src/components/Permissions/WithUserPermissions";
import { useEnrollmentPeriod } from "src/components/Providers/EnrollmentPeriodProvider";
import { AdminHeading } from "src/components/Text/AdminHeading";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useGlossary } from "src/hooks/useGlossary";
import { useMatchConfiguration } from "src/hooks/useMatchConfiguration";
import { useOrganization } from "src/hooks/useOrganization";
import { useRemoteDataMutation } from "src/hooks/useRemoteDataMutation";
import * as breadcrumb from "src/services/breadcrumb";
import { OrgAdmin } from "src/services/url";
import * as GQL from "src/types/graphql";
import { ConfigureMatchButton } from "./components/ConfigureMatchButton";
import { MatchType, matchTypes } from "./components/ConfigureMatchDialog";
import { ConfigureMatchSchoolList } from "./components/ConfigureMatchSchoolList";
import { UPSERT_MATCH_CONFIGURATION } from "./graphql/mutations";
import { GET_MATCH_CONFIGURATION } from "./graphql/queries";

export function Configure() {
  const navigate = useNavigate();
  const toast = useAvelaToast();
  const { state } = useLocation();
  const { glossary } = useGlossary();
  const organization = useOrganization();
  const { matchConfig } = useMatchConfiguration();
  const { selectedEnrollmentPeriod } = useEnrollmentPeriod();

  const [selectedType, setSelectedType] =
    React.useState<MatchType>("multi-offer");

  useEffect(() => {
    if (matchConfig?.parameters.match_type) {
      setSelectedType(matchConfig.parameters.match_type);
    } else if (state?.matchType) {
      setSelectedType(state.matchType);
    }
  }, [state, matchConfig]);

  const selectedMatchObject = matchTypes.find(
    (matchType) => matchType.type === selectedType
  );

  const [upsertMatchConfiguration] = useRemoteDataMutation<
    GQL.UpsertMatchConfiguration,
    GQL.UpsertMatchConfigurationVariables
  >(UPSERT_MATCH_CONFIGURATION);

  const handleSubmitMatchConfiguration = useCallback(async () => {
    try {
      if (!selectedEnrollmentPeriod?.id) {
        throw new Error(
          "Enrollment period is required uppon match config creation."
        );
      }

      await upsertMatchConfiguration({
        variables: {
          matchConfig: {
            id: matchConfig?.id,
            name: `match-config-${selectedType}-${selectedEnrollmentPeriod?.name}`,
            enrollment_period_id:
              matchConfig?.enrollment_period_id ?? selectedEnrollmentPeriod?.id,
            parameters: { match_type: selectedType },
          },
        },
        refetchQueries: [GET_MATCH_CONFIGURATION],
        awaitRefetchQueries: true,
      });

      if (!toast.isActive("match-configuration-saved")) {
        toast({
          id: "match-configuration-saved",
          title: glossary`Match configuration saved`,
          isClosable: true,
          status: "success",
        });
      }

      navigate(
        organization.map((org) => OrgAdmin.Match.index(org)).withDefault("#")
      );
    } catch (error) {
      console.error(error);
      if (!toast.isActive("match-configuration-saved")) {
        toast({
          id: "match-configuration-saved",
          title: glossary`Error saving match configuration`,
          description:
            "Please try again later or report the problem to our support team.",
          isClosable: true,
          status: "error",
        });
      }
    }
  }, [
    selectedType,
    upsertMatchConfiguration,
    glossary,
    toast,
    navigate,
    organization,
    selectedEnrollmentPeriod,
    matchConfig,
  ]);

  return (
    <AdminPage
      breadcrumb={
        <Breadcrumb
          items={breadcrumb.match.getBreadcrumbsForConfigure(organization)}
        />
      }
      heading={<AdminHeading title="Match rules" />}
    >
      <Flex direction="column" gap={6} height="100%">
        <Flex direction="column" gap={4}>
          <Text>Match type</Text>

          <Card
            display="flex"
            showBorder
            padding={6}
            width="100%"
            justifyContent="space-between"
          >
            <Flex direction="column" gap={4} width="100%">
              <Flex justifyContent="space-between">
                <Text fontSize="xl" color="gray.800">
                  {selectedMatchObject?.title}
                </Text>

                <WithUserPermissions
                  permissions={["match.configuration:update"]}
                >
                  <ConfigureMatchButton
                    editMode
                    variant="outline"
                    colorScheme="gray"
                    size="sm"
                    leftIcon={<RiEditLine />}
                    onMatchTypeChange={setSelectedType}
                  />
                </WithUserPermissions>
              </Flex>
              <Text>{selectedMatchObject?.description}</Text>
            </Flex>
          </Card>
        </Flex>

        <Text>School rules</Text>

        <ConfigureMatchSchoolList />
      </Flex>

      <Spacer minH={8} />

      <WithUserPermissions
        permissions={["match.configuration:update"]}
        orElse={
          <AdminFormButtons justifyContent="space-between">
            <Button
              variant="outline"
              colorScheme="gray"
              onClick={() =>
                navigate(
                  organization
                    .map((org) => OrgAdmin.Match.index(org))
                    .withDefault("#")
                )
              }
            >
              Back
            </Button>
          </AdminFormButtons>
        }
      >
        <AdminFormButtons justifyContent="space-between">
          <Button
            variant="outline"
            colorScheme="gray"
            onClick={() =>
              navigate(
                organization
                  .map((org) => OrgAdmin.Match.index(org))
                  .withDefault("#")
              )
            }
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitMatchConfiguration}
            isDisabled={!selectedType}
          >
            Done
          </Button>
        </AdminFormButtons>
      </WithUserPermissions>
    </AdminPage>
  );
}
